import React, { useEffect, useState } from 'react';
import { ArrowLeft, ScrollText } from 'lucide-react';
import { LedgerLine, listAccountLedger } from '../../../../../services/JournalEntriesService';
import { useToast } from '../../../../../hooks/useToast';
import { formatDate } from '../forms/FormParts';
import '../../shared-modules.css';
import '../forms/FinanceForms.css';
import '../QuickActionsHub.css';
import '../../FixedAssetsView/FixedAssetsView.css';

const LEDGER_LIMIT = 500;

// Sin €, como en SAP: es el importe en la moneda de la sociedad
const formatAmount = (value: number, privacyMode: boolean): string =>
  privacyMode
    ? '•••••'
    : new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

/** Cuenta contable de un tercero (430NNNN de un cliente, 400NNNN/410NNNN de un proveedor). */
export interface LedgerAccount {
  accountId: string;
  code: string;
  name: string;
}

type PartyKind = 'deudor' | 'acreedor';

const KIND_TEXT: Record<PartyKind, { group: string; party: string; who: string; own: string }> = {
  deudor: { group: 'DEUDORES', party: 'Cliente', who: 'cliente', own: 'Movimientos de la cuenta del cliente' },
  acreedor: { group: 'ACREEDORES', party: 'Proveedor', who: 'proveedor', own: 'Movimientos de la cuenta del proveedor' }
};

interface PartyLedgerViewProps {
  kind: PartyKind;
  party: { id: string; name: string; cif: string };
  /** Cuenta propia del tercero en la sociedad activa; undefined si todavía no tiene. */
  account?: LedgerAccount;
  privacyMode: boolean;
  onClose: () => void;
  /** Crea la cuenta del tercero (la siguiente libre de su familia) y la devuelve. */
  createAccount: () => Promise<LedgerAccount>;
  /** Se llama al crear la cuenta desde esta pantalla, para que el maestro la recuerde. */
  onAccountCreated: (account: LedgerAccount) => void;
}

/**
 * "Ver partidas" de un deudor o un acreedor: los movimientos (apuntes) de la cuenta contable asociada
 * al tercero, del más reciente al más antiguo, con el saldo acumulado tras cada apunte.
 */
export const PartyLedgerView = ({ kind, party, account, privacyMode, onClose, createAccount, onAccountCreated }: PartyLedgerViewProps) => {
  const text = KIND_TEXT[kind];
  const { notify } = useToast();
  const [lines, setLines] = useState<LedgerLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!account) {
      setLines([]);
      setError('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    listAccountLedger(account.accountId, LEDGER_LIMIT)
      .then(list => { if (!cancelled) { setLines(list); setError(''); } })
      .catch(err => { if (!cancelled) { setLines([]); setError((err as Error)?.message || 'No se pudieron cargar los movimientos.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [account?.accountId]);

  const handleCreateAccount = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await createAccount();
      onAccountCreated(created);
      notify('Éxito', `Cuenta ${created.code} creada para "${party.name}".`, 'info');
    } catch (err) {
      setError((err as Error)?.message || `No se pudo crear la cuenta del ${text.who}.`);
    } finally {
      setCreating(false);
    }
  };

  const totalDebit = lines.reduce((sum, l) => sum + l.debit, 0);
  const totalCredit = lines.reduce((sum, l) => sum + l.credit, 0);
  // La primera línea es la más reciente: su saldo acumulado es el saldo actual de la cuenta
  const currentBalance = lines.length > 0 ? lines[0].balance : 0;

  return (
    <>
      <div className="qa-back-bar">
        <div className="qa-back-bar-left">
          <button type="button" onClick={onClose} className="qa-back-btn">
            <ArrowLeft size={14} /> Volver
          </button>
          <div>
            <span className="qa-back-bar-category">{text.group}</span>
            <h2 className="qa-back-bar-title">{text.own}</h2>
          </div>
        </div>
        <span className="qa-back-bar-hint">Apuntes contabilizados de su cuenta contable</span>
      </div>

      <div className="fin-doc">
        <div className="fin-doc__header">
          <div>
            <span className="fin-doc__tag">{text.group} • LIBRO MAYOR</span>
            <h3 className="fin-doc__title"><ScrollText size={14} /> {account ? `${account.code} · ${party.name}` : party.name}</h3>
          </div>
          {account && !loading && !error && (
            <span className="fin-doc__badge">SALDO {formatAmount(currentBalance, privacyMode)}</span>
          )}
        </div>

        <div className="fin-doc__grid fin-doc__grid--account">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">{text.party}</label>
            <input type="text" readOnly className="sidebar-form-input fin-doc__readonly" value={party.name} />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">CIF / NIF</label>
            <input type="text" readOnly className="sidebar-form-input fin-doc__mono fin-doc__readonly" value={party.cif} />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Cuenta contable</label>
            <input type="text" readOnly className="sidebar-form-input fin-doc__mono fin-doc__readonly" value={account ? `${account.code} · ${account.name}` : 'Sin cuenta propia'} />
          </div>
        </div>

        {!account ? (
          <div className="module-panel-empty">
            <p>Este {text.who} todavía no tiene cuenta contable propia en esta sociedad, así que no hay movimientos que mostrar.</p>
            <p>Se crea sola con su primer movimiento, o puedes crearla ahora.</p>
            {error && <p>{error}</p>}
            <button type="button" className="module-action-btn" onClick={handleCreateAccount} disabled={creating}>
              {creating ? 'Creando...' : 'Crear cuenta propia'}
            </button>
          </div>
        ) : (
          <div className="fixed-assets-view__table-wrapper">
            <table className="fixed-assets-view__table">
              <thead>
                <tr>
                  <th>FECHA</th>
                  <th>CONCEPTO</th>
                  <th>ORIGEN</th>
                  <th className="right">DEBE</th>
                  <th className="right">HABER</th>
                  <th className="right">SALDO</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="module-panel-empty">Cargando movimientos...</td></tr>
                ) : error ? (
                  <tr><td colSpan={6} className="module-panel-empty">{error}</td></tr>
                ) : lines.length === 0 ? (
                  <tr><td colSpan={6} className="module-panel-empty">La cuenta no tiene apuntes contabilizados.</td></tr>
                ) : (
                  <>
                    {lines.map((l, i) => (
                      <tr key={`${l.entryId}-${i}`} className="fixed-assets-view__table-row">
                        <td>{l.date ? formatDate(l.date) : '—'}</td>
                        <td>{l.concept}</td>
                        <td>{l.sourceModule || '—'}</td>
                        <td className="right">{l.debit ? formatAmount(l.debit, privacyMode) : ''}</td>
                        <td className="right">{l.credit ? formatAmount(l.credit, privacyMode) : ''}</td>
                        <td className="right">{formatAmount(l.balance, privacyMode)}</td>
                      </tr>
                    ))}
                    <tr className="fixed-assets-view__table-total">
                      <td colSpan={3}>TOTAL ({lines.length}{lines.length >= LEDGER_LIMIT ? ', últimos movimientos' : ''})</td>
                      <td className="right">{formatAmount(totalDebit, privacyMode)}</td>
                      <td className="right">{formatAmount(totalCredit, privacyMode)}</td>
                      <td className="right">{formatAmount(currentBalance, privacyMode)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default PartyLedgerView;
